import { z } from 'zod'
import { FRENTE_KEYS, type Coverage, type ErpRules, type ErpSettings, type ErpSettingsPatch, type FrenteKey } from '../contract'
import { ADMIN_ROLES, type AdminRole } from '../../admin/shared/permissions'
import { ERP_VIEWS, EXPERIENCES, experienceOfView, FRENTES, frenteEntry, frenteNav, ROLE_FRENTES, viewFullLabel, type ExperienceKey } from '../frentes'

/* ERP · Configurações — regras puras usadas pelo servidor (validação, mescla, histórico) e pelas telas (menus por
   frente, prévia dos alertas). Sem React e sem acesso a rede. */

// ---------------------------------------------------------------- limites

/** Limites de cada número das regras (o servidor rejeita fora deles; as telas usam os mesmos no min/max). */
export const RULE_LIMITS = {
    letterDays: { min: 1, max: 120 },
    specialRequestDays: { min: 1, max: 60 },
    childDeactivateDays: { min: 30, max: 365 },
    childAlertDays: { min: 1, max: 364 },
    churchRevisitDays: { min: 7, max: 365 },
    liveConversionTarget: { min: 1, max: 100 },
    fieldTarget: { min: 50, max: 100 },
    splitOsp: { min: 1, max: 99 },
    sessionHours: { min: 1, max: 24 },
} as const

const int = (l: { min: number; max: number }) => z.number().int().min(l.min).max(l.max)
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/

const viewKey = z.string().refine(k => k in ERP_VIEWS, 'tela desconhecida')

const frentePatchSchema = z.object({
    nav: z.array(viewKey).min(1).max(Object.keys(ERP_VIEWS).length).refine(a => new Set(a).size === a.length, 'tela repetida').optional(),
    pii: z.boolean().optional(),
}).strict()

const rulesPatchSchema = z.object({
    letterDays: z.object({ boasVindas: int(RULE_LIMITS.letterDays), amistosa: int(RULE_LIMITS.letterDays), dfc: int(RULE_LIMITS.letterDays), resposta: int(RULE_LIMITS.letterDays) }).strict(),
    specialRequestDays: int(RULE_LIMITS.specialRequestDays),
    childDeactivateDays: int(RULE_LIMITS.childDeactivateDays),
    childAlertDays: int(RULE_LIMITS.childAlertDays),
    churchRevisitDays: int(RULE_LIMITS.churchRevisitDays),
    liveConversionTarget: int(RULE_LIMITS.liveConversionTarget),
    fieldTarget: int(RULE_LIMITS.fieldTarget),
    split: z.object({ osp: int(RULE_LIMITS.splitOsp), cfb: int(RULE_LIMITS.splitOsp) }).strict().refine(s => s.osp + s.cfb === 100, 'rateio precisa somar 100'),
    overdueAlert: z.object({ on: z.boolean(), time: z.string().regex(HHMM) }).strict(),
}).strict().partial()

const isTimezone = (tz: string) => {
    try {
        new Intl.DateTimeFormat('pt-BR', { timeZone: tz })
        return true
    } catch {
        return false
    }
}

const orgPatchSchema = z.object({
    name: z.string().trim().min(2).max(120),
    city: z.string().trim().max(120),
    timezone: z.string().max(64).refine(isTimezone, 'fuso inválido'),
    ssoMicrosoft: z.boolean(),
    require2fa: z.boolean(),
    sessionHours: int(RULE_LIMITS.sessionHours),
}).strict().partial()

/** PUT /admin/erp/settings. Tudo opcional; chaves desconhecidas são rejeitadas. */
export const erpSettingsPatchSchema = z.object({
    frentes: z.object(Object.fromEntries(FRENTE_KEYS.map(k => [k, frentePatchSchema.optional()])) as Record<FrenteKey, z.ZodOptional<typeof frentePatchSchema>>).strict().optional(),
    rules: rulesPatchSchema.optional(),
    org: orgPatchSchema.optional(),
}).strict()
/** O que o servidor aceita: ErpSettingsPatch, com `org` também parcial. */
export type ErpSettingsInput = z.infer<typeof erpSettingsPatchSchema>

// ---------------------------------------------------------------- menus por frente

/** Telas do menu padrão de uma frente, na ordem. */
export const defaultNavKeys = (frente: FrenteKey): string[] => FRENTES[frente].nav.flatMap(([, keys]) => [...keys])

/** Telas do menu da frente (Configurações ou padrão), na ordem. */
export const navKeysOf = (frente: FrenteKey, settings?: ErpSettings | null): string[] => settings?.frentes[frente]?.nav ?? defaultNavKeys(frente)

const sameList = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x, i) => x === b[i])

export const isCustomNav = (frente: FrenteKey, settings?: ErpSettings | null) => !sameList(navKeysOf(frente, settings), defaultNavKeys(frente))

/** Liga/desliga uma tela no menu (no fim, quando entra). Nunca deixa o menu vazio. */
export function toggleView(keys: readonly string[], key: string, on: boolean): string[] {
    const has = keys.includes(key)
    if (on === has || !(key in ERP_VIEWS)) return [...keys]
    if (!on) return keys.length <= 1 ? [...keys] : keys.filter(k => k !== key)
    return [...keys, key]
}

/** Sobe (-1) ou desce (+1) uma tela no menu. */
export function moveView(keys: readonly string[], key: string, dir: -1 | 1): string[] {
    const out = [...keys]
    const i = out.indexOf(key)
    const j = i + dir
    if (i < 0 || j < 0 || j >= out.length) return out
    ;[out[i], out[j]] = [out[j] as string, out[i] as string]
    return out
}

// ---------------------------------------------------------------- frente × experiência

/** Célula da matriz: quantas telas da experiência a frente tem no menu (completa, parcial ou nenhuma). */
export type ExperienceCell = { on: number; total: number; state: 'full' | 'part' | 'none' }

export function experienceCell(keys: readonly string[], xp: ExperienceKey): ExperienceCell {
    const views = EXPERIENCES[xp].views
    const on = views.filter(k => keys.includes(k)).length
    return { on, total: views.length, state: !on ? 'none' : on === views.length ? 'full' : 'part' }
}

/** Texto da célula: "Completa", "3 de 10" ou "—". */
export const cellLabel = (c: ExperienceCell) => (c.state === 'none' ? '—' : c.state === 'full' ? 'Completa' : `${c.on} de ${c.total}`)

/** Troca as telas de uma experiência no menu pelo bloco `next`, no lugar onde o bloco estava (ou no fim, se não estava).
    As telas das outras experiências ficam onde estão. Nunca deixa o menu vazio. */
function replaceExperience(keys: readonly string[], xp: ExperienceKey, next: readonly string[]): string[] {
    const views = EXPERIENCES[xp].views
    const first = keys.findIndex(k => views.includes(k))
    const rest = keys.filter(k => !views.includes(k))
    const at = first < 0 ? rest.length : keys.slice(0, first).filter(k => !views.includes(k)).length
    const out = [...rest.slice(0, at), ...next, ...rest.slice(at)]
    return out.length ? out : [...keys]
}

/** "Liberar tudo" (todas, na ordem do catálogo), "Só a visão" ou "Tirar" a experiência do menu da frente. */
export function setExperienceViews(keys: readonly string[], xp: ExperienceKey, mode: 'all' | 'vision' | 'none'): string[] {
    const e = EXPERIENCES[xp]
    return replaceExperience(keys, xp, mode === 'all' ? e.views : mode === 'vision' ? [e.overview] : [])
}

/** Liga/desliga uma tela da experiência: ligar qualquer tela liga também a visão (a porta da experiência); desligar a
    visão tira a experiência inteira. Telas fora das experiências (Início) seguem toggleView. */
export function toggleExperienceView(keys: readonly string[], key: string, on: boolean): string[] {
    const e = experienceOfView(key)
    if (!e) return toggleView(keys, key, on)
    const cur = keys.filter(k => e.views.includes(k))
    if (on === cur.includes(key)) return [...keys]
    const next = on
        ? [...(cur.includes(e.overview) || key === e.overview ? [] : [e.overview]), ...(key === e.overview ? [key, ...cur] : [...cur, key])]
        : key === e.overview ? [] : cur.filter(k => k !== key)
    return replaceExperience(keys, e.key, next)
}

/** Sobe (-1) ou desce (+1) uma tela entre as telas da MESMA experiência (a ordem das abas). */
export function moveWithinExperience(keys: readonly string[], key: string, dir: -1 | 1): string[] {
    const e = experienceOfView(key)
    const out = [...keys]
    const i = out.indexOf(key)
    if (!e || i < 0) return out
    let j = i + dir
    while (j >= 0 && j < out.length && !e.views.includes(out[j] as string)) j += dir
    if (j < 0 || j >= out.length) return out
    ;[out[i], out[j]] = [out[j] as string, out[i] as string]
    return out
}

/** Experiência por onde a frente entra hoje (a de entrada, se estiver no menu; senão a da 1ª tela). */
export function entryExperienceOf(frente: FrenteKey, settings?: ErpSettings | null): ExperienceKey | null {
    const path = frenteEntry(frente, frenteNav(frente, 'admin', settings))
    const view = Object.values(ERP_VIEWS).find(v => v.path === path)
    return view ? experienceOfView(view.key)?.key ?? null : null
}

// ---------------------------------------------------------------- mescla e histórico

/** Configuração nova = atual + patch (já validado). O menu igual ao padrão volta a ser "padrão" (não fica guardado). */
export function mergeErpSettings(current: ErpSettings, patch: ErpSettingsInput | ErpSettingsPatch, meta: { at: string; by: string }): ErpSettings {
    const frentes: ErpSettings['frentes'] = structuredClone(current.frentes)
    for (const k of FRENTE_KEYS) {
        const p = patch.frentes?.[k]
        if (!p) continue
        const next = { ...frentes[k], ...p }
        if (next.nav && sameList(next.nav, defaultNavKeys(k))) delete next.nav
        if (next.pii === FRENTES[k].pii) delete next.pii
        if (next.nav === undefined && next.pii === undefined) delete frentes[k]
        else frentes[k] = next
    }
    const rules: ErpRules = { ...structuredClone(current.rules), ...structuredClone(patch.rules ?? {}) }
    const org = { ...current.org, ...(patch.org ?? {}) }
    return { frentes, rules, org, updatedAt: meta.at, updatedBy: meta.by }
}

/** Regras que dependem de mais de um campo (depois da mescla). null = ok. */
export function settingsProblem(s: ErpSettings): string | null {
    if (s.rules.childAlertDays >= s.rules.childDeactivateDays) return 'O aviso da criança precisa vir antes do prazo de desativação.'
    if (s.rules.split.osp + s.rules.split.cfb !== 100) return 'O rateio precisa somar 100%.'
    return null
}

/** Uma linha do histórico das Configurações do ERP. */
export type ErpSettingsChange = { at: string; by: string; changes: string[] }
export const HISTORY_LIMIT = 50

const RULE_LABELS: Record<string, string> = {
    'letterDays.boasVindas': 'Prazo da carta de boas-vindas',
    'letterDays.amistosa': 'Prazo da carta amistosa',
    'letterDays.dfc': 'Prazo da carta de DFC',
    'letterDays.resposta': 'Prazo da resposta ao padrinho',
    specialRequestDays: 'Prazo da OSP no pedido especial',
    childDeactivateDays: 'Prazo da criança sem padrinho',
    childAlertDays: 'Aviso de criança perto do limite',
    churchRevisitDays: 'Igreja sem visita',
    liveConversionTarget: 'Meta de conversão do Live Concert',
    fieldTarget: 'Meta para o campo',
    'split.osp': 'Rateio · parte das OSPs',
    'split.cfb': 'Rateio · parte do ChildFund Brasil',
    'overdueAlert.on': 'Alerta diário de inadimplência',
    'overdueAlert.time': 'Horário do alerta de inadimplência',
}
const ORG_LABELS: Record<string, string> = {
    name: 'Nome da organização', city: 'Cidade sede', timezone: 'Fuso horário', ssoMicrosoft: 'Entrar com Microsoft (SSO)', require2fa: '2FA obrigatório', sessionHours: 'Duração da sessão (horas)',
}

const fmt = (v: unknown) => (typeof v === 'boolean' ? (v ? 'ligado' : 'desligado') : String(v))

function flat(o: Record<string, unknown>, prefix = ''): Record<string, unknown> {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(o)) {
        if (v && typeof v === 'object' && !Array.isArray(v)) Object.assign(out, flat(v as Record<string, unknown>, `${prefix}${k}.`))
        else out[`${prefix}${k}`] = v
    }
    return out
}

/** O que mudou, em frases curtas para o histórico (sem dado pessoal). */
export function describeChanges(prev: ErpSettings, next: ErpSettings): string[] {
    const out: string[] = []
    for (const k of FRENTE_KEYS) {
        const label = FRENTES[k].label
        const a = navKeysOf(k, prev), b = navKeysOf(k, next)
        if (!sameList(a, b)) {
            if (!next.frentes[k]?.nav) out.push(`${label}: menu voltou ao padrão`)
            else {
                const added = b.filter(x => !a.includes(x)).map(viewFullLabel)
                const removed = a.filter(x => !b.includes(x)).map(viewFullLabel)
                const parts = [added.length ? `entrou ${added.join(', ')}` : '', removed.length ? `saiu ${removed.join(', ')}` : '', !added.length && !removed.length ? 'ordem alterada' : '']
                out.push(`${label}: menu (${parts.filter(Boolean).join('; ')})`)
            }
        }
        const pa = prev.frentes[k]?.pii ?? FRENTES[k].pii, pb = next.frentes[k]?.pii ?? FRENTES[k].pii
        if (pa !== pb) out.push(`${label}: vê dados pessoais ${pb ? 'ligado' : 'desligado'}`)
    }
    const ra = flat(prev.rules), rb = flat(next.rules)
    for (const key of Object.keys(rb)) if (ra[key] !== rb[key]) out.push(`${RULE_LABELS[key] ?? key}: de ${fmt(ra[key])} para ${fmt(rb[key])}`)
    for (const key of Object.keys(next.org) as (keyof ErpSettings['org'])[]) {
        if (prev.org[key] !== next.org[key]) out.push(`${ORG_LABELS[key] ?? key}: de ${fmt(prev.org[key])} para ${fmt(next.org[key])}`)
    }
    return out
}

/** Tipo de uma frase do histórico (para os segmentos da Auditoria). */
export type ChangeKind = 'menu' | 'pii' | 'rule' | 'org'
const ORG_PREFIXES = Object.values(ORG_LABELS)

export function changeKind(text: string): ChangeKind {
    if (/: menu( |$)/.test(text)) return 'menu'
    if (/: vê dados pessoais /.test(text)) return 'pii'
    if (ORG_PREFIXES.some(l => text.startsWith(`${l}:`))) return 'org'
    return 'rule'
}

export const CHANGE_KIND_LABELS: Record<ChangeKind, string> = { menu: 'Menus', pii: 'Dados pessoais', rule: 'Regras', org: 'Organização' }

/** Uma linha por frase do histórico (a mais recente primeiro, como o servidor devolve). */
export type ChangeLine = { at: string; by: string; text: string; kind: ChangeKind }

export function flattenHistory(items: readonly ErpSettingsChange[]): ChangeLine[] {
    return items.flatMap(it => it.changes.map(text => ({ at: it.at, by: it.by, text, kind: changeKind(text) })))
}

// ---------------------------------------------------------------- prévia dos alertas

type PreviewIndex = {
    overdue(): { days: number }[]
    near180(): { daysWithout: number | null }[]
    lettersDue(): { letter: { due: string } }[]
    all(type: 'igreja'): { lastVisit: number | null }[]
}

export type AlertPreview = { overdue: number; nearLimit: number; letters: number; lettersLate: number; churches: number }

const addDays = (iso: string, days: number) => new Date(Date.parse(`${iso}T12:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10)

/** Quantos itens cada regra dispararia hoje (cartas: vencendo nos próximos 7 dias + atrasadas). */
export function alertPreview(q: PreviewIndex, rules: ErpRules, today: string): AlertPreview {
    const kids = q.near180().filter(k => k.daysWithout !== null && k.daysWithout >= rules.childAlertDays && k.daysWithout < rules.childDeactivateDays)
    const horizon = addDays(today, 7)
    const letters = q.lettersDue().filter(x => x.letter.due <= horizon)
    return {
        overdue: q.overdue().length,
        nearLimit: kids.length,
        letters: letters.length,
        lettersLate: letters.filter(x => x.letter.due < today).length,
        churches: q.all('igreja').filter(i => i.lastVisit !== null && i.lastVisit >= rules.churchRevisitDays).length,
    }
}

// ---------------------------------------------------------------- perfis

/** Perfis que abrem uma frente (ROLE_FRENTES). */
export const rolesOpening = (frente: FrenteKey): AdminRole[] => ADMIN_ROLES.filter(r => ROLE_FRENTES[r].includes(frente))

// ---------------------------------------------------------------- integrações

export const COVERAGE_LABELS: Record<keyof Coverage, string> = {
    contracts: 'Contratos e linhas',
    invoices: 'Faturas e pagamentos',
    cases: 'Ocorrências',
    letters: 'Cartas e prazos',
    gifts: 'Presentes (DFC)',
    visits: 'Visitas',
    children: 'Crianças',
    consultants: 'Consultores',
    fichas: 'Fichas digitais',
}

/** O que a base já traz e o que falta sincronizar (rótulos, na ordem de COVERAGE_LABELS). */
export function coverageSummary(c: Coverage): { synced: string[]; missing: string[] } {
    const keys = Object.keys(COVERAGE_LABELS) as (keyof Coverage)[]
    return { synced: keys.filter(k => c[k]).map(k => COVERAGE_LABELS[k]), missing: keys.filter(k => !c[k]).map(k => COVERAGE_LABELS[k]) }
}

/** Sistemas das Integrações: os problemas primeiro (sem resposta, aguardando, em testes, sem informação, funcionando),
    depois por nome. */
const SYSTEM_ORDER: Record<string, number> = { down: 0, waiting: 1, testing: 2, unknown: 3, ok: 4 }

export function sortSystems<T extends { status: string; name: string }>(list: readonly T[]): T[] {
    return [...list].sort((a, b) => (SYSTEM_ORDER[a.status] ?? 5) - (SYSTEM_ORDER[b.status] ?? 5) || a.name.localeCompare(b.name, 'pt-BR'))
}

// ---------- Auditoria · Ações no painel (panel_audit) ----------

/** Pedaços do nome técnico da ação ("admin.rede.ambassador.agent_search") em português; o que não estiver aqui sai como veio. */
const AUDIT_WORDS: Record<string, string> = {
    rede: 'Rede', ambassador: 'embaixadores', corporate: 'empresas', church: 'igrejas', embaixadores: 'Embaixadores', parceiros: 'Parceiros',
    moderacao: 'Moderação', equipe: 'Equipe', erp: 'ERP', fichas: 'Fichas', cartas: 'Cartas', inscricoes: 'Inscrições', users: 'Usuários',
    pre_ativos: 'Pré-ativos', retencao: 'Retenção', recorrencias: 'Recorrências', conciliacao: 'Conciliação', marketing: 'Marketing',
    indicadores: 'Indicadores', settings: 'configurações', export: 'exportação',
    agent_search: 'busca com agente', agent_enrich: 'completar com agente', agent_photos: 'fotos pelo agente', ai_search: 'busca com IA',
    found_search: 'busca na base de dados', map_search: 'busca no mapa', approve: 'aprovou', reject: 'reprovou', reconsider: 'reconsiderou',
    return: 'retorno', node: 'conteúdo da régua', rewrite: 'reescreveu com IA', create: 'cadastrou', create_undo: 'desfez cadastro',
    update: 'alterou', save: 'salvou', send: 'enviou', schedule: 'agendou visita', schedule_undo: 'desfez agendamento', invite: 'convidou',
    invite_cancel: 'cancelou convite', offboard: 'desligou', photo: 'foto', unmask: 'mostrou dado pessoal', reveal: 'mostrou dado pessoal',
    access_link: 'link de acesso', client_send: 'enviou ao cliente', client_decide: 'decisão do cliente', client_reset: 'limpou respostas',
}

/** "admin.rede.ambassador.agent_search" -> "Rede · embaixadores · busca com agente". */
export function auditActionLabel(action: string): string {
    const parts = action.split('.').filter(p => p && p !== 'admin')
    return parts.map(p => AUDIT_WORDS[p] ?? p.replace(/_/g, ' ')).join(' · ') || action
}

/** Resultado da linha: "ok" ou não (failed, 4xx/5xx…). */
export const auditOk = (result: string) => result === 'ok'
